import styled, { keyframes, css } from 'styled-components'

// Animation configuration
const ANIMATION_SPEED = '6s'

// Color palette
const COLORS = {
  yellow: '#f3dd87',
  orange: '#feb594',
  green: '#bdff8d',
} as const

// Opacity values for variation
const OPACITY = {
  low: 0.2,
  medium: 0.6,
  high: 0.7,
  peak1: 0.92,
  peak2: 0.88,
  peak3: 0.65,
  peak4: 0.85,
  peak5: 0.91,
  peak6: 0.89,
} as const

// Animation keyframes with staggered timing
const fadeInOut1 = keyframes`
  0%, 100% {
    opacity: ${OPACITY.high};
  }
  30%, 70% {
    opacity: ${OPACITY.peak1};
  }
`

const fadeInOut2 = keyframes`
  0%, 16.66%, 83.33%, 100% {
    opacity: ${OPACITY.high};
  }
  46.66%, 53.33% {
    opacity: ${OPACITY.peak2};
  }
`

const fadeInOut3 = keyframes`
  0%, 33.33%, 66.66%, 100% {
    opacity: ${OPACITY.medium};
  }
  49.99%, 63.33% {
    opacity: ${OPACITY.peak3};
  }
`

const fadeInOut4 = keyframes`
  0%, 49.99%, 100% {
    opacity: ${OPACITY.high};
  }
  66.66%, 83.33% {
    opacity: ${OPACITY.peak4};
  }
`

const fadeInOut5 = keyframes`
  0%, 66.66%, 83.33%, 100% {
    opacity: ${OPACITY.high};
  }
  83.33%, 96.66% {
    opacity: ${OPACITY.peak5};
  }
`

const fadeInOut6 = keyframes`
  0%, 83.33%, 100% {
    opacity: ${OPACITY.low};
  }
  16.66%, 33.33% {
    opacity: ${OPACITY.peak6};
  }
`

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

// Common circle base styles to reduce duplication
const CircleBase = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  z-index: -1;
  filter: blur(30px);

  @media (prefers-color-scheme: dark) {
    opacity: 0.2;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 50%;
    left: 50%;
  }
`

// Circle components with cleaner, more maintainable styles
export const Circle1 = styled(CircleBase)`
  &::before {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, ${COLORS.yellow} 0%);
    transform: translate(calc(-50% + 30px), calc(-50% + 40px));
    animation: ${fadeInOut1} ${ANIMATION_SPEED} ease-in-out infinite;
  }

  &::after {
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, ${COLORS.orange} 0%);
    transform: translate(calc(-50% - 60px), calc(-50% + 50px));
    animation: ${fadeInOut2} ${ANIMATION_SPEED} ease-in-out infinite;
  }
`

export const Circle2 = styled(CircleBase)`
  &::before {
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, ${COLORS.yellow} 25%);
    transform: translate(calc(-50% - 60px), calc(-50% - 40px));
    animation: ${fadeInOut5} ${ANIMATION_SPEED} ease-in-out infinite;
  }

  &::after {
    width: 160px;
    height: 160px;
    background: radial-gradient(circle, ${COLORS.orange} 15%);
    transform: translate(calc(-50% + 50px), calc(-50% - 60px));
    animation: ${fadeInOut6} ${ANIMATION_SPEED} ease-in-out infinite;
  }
`

export const Circle3 = styled(CircleBase)`
  &::before {
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, ${COLORS.green} 25%, transparent 100%);
    transform: translate(calc(-50% - 60px), calc(-50% + 30px));
    animation: ${fadeInOut3} ${ANIMATION_SPEED} ease-in-out infinite;
  }

  &::after {
    width: 160px;
    height: 160px;
    background: radial-gradient(circle, ${COLORS.orange} 15%);
    transform: translate(calc(-50% + 50px), calc(-50% - 40px));
    animation: ${fadeInOut4} ${ANIMATION_SPEED} ease-in-out infinite;
  }
`

export const ImageWrapper = styled.div<{ $isDarkMode?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
  flex: 1 1 100%;

  ${({ $isDarkMode }) =>
    $isDarkMode &&
    css`
      ${CircleBase} {
        opacity: 0.2;
      }
    `}
`

export const ContentContainer = styled.div`
  padding: 32px 0;
  margin: 32px 0;
`
